import { useEffect, useMemo, useRef } from 'react';
import type { FolderAnalysisProgress, FolderAnalysisStatus } from '../shared/desktop-api';
import { workingStageRank } from './working-progress';
import { forgetWorkingRuns } from './useWorkingProgress';
import { forgetSetupPage, forgetSetupResults } from './setup-handoff';

/**
 * Where a job's setup screen had got to, kept across a remount and an app restart.
 *
 * The setup screen's progress state lived in a ref: the failure screen after an
 * engine crash, Resume, or reopening Paige mounted a fresh one, and a resumed
 * setup re-reports from Prepare, so the steps went Prepare..Understand and back to
 * Prepare (owner, 2026-09-29, installed 6.2.0; reproduced on the real screen by
 * tests/workspace-visual-release-tests/setup-stepper-proof-run.mjs). The furthest
 * event shown is saved per job with the setup operations it belongs to, and folded in
 * AHEAD of the live one (same lane, so the reducer's stage and counts only rise). A
 * different setup (a new operation id on a fresh mount) starts clean; a finished setup
 * forgets it. Display only: nothing here is sent to the engine or main.
 */
// `steps`: each setup step's own furthest event (Understand's count and bar are per step).
type Saved = {
  operations: string[];
  event: FolderAnalysisProgress;
  steps?: Record<string, FolderAnalysisProgress>;
};
const storageKey = (jobId: string) => `cpv6-setup-progress-floor:${jobId}`;

const read = (jobId: string): Saved | null => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(jobId)) ?? 'null') as Saved | null;
    return saved &&
      Array.isArray(saved.operations) &&
      saved.event &&
      typeof saved.event.stage === 'string'
      ? saved
      : null;
  } catch {
    return null;
  }
};
const write = (jobId: string, saved: Saved | null) => {
  try {
    if (saved) localStorage.setItem(storageKey(jobId), JSON.stringify(saved));
    else localStorage.removeItem(storageKey(jobId));
  } catch {
    /* storage unavailable: the screen still works, it just cannot remember */
  }
};

const counted = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
/** The event as it is worth keeping: counts at their highest, no page evidence or finished document. */
function merge(
  saved: FolderAnalysisProgress | null,
  event: FolderAnalysisProgress,
): FolderAnalysisProgress {
  const { completedDocument: _document, ...kept } = event as FolderAnalysisProgress & {
    completedDocument?: unknown;
  };
  const activity = kept.activity ? { ...kept.activity, evidence: null } : saved?.activity;
  const sameStage =
    saved && saved.stage === event.stage && counted(saved.total) === counted(event.total);
  const sameStep = saved?.activity && activity && saved.activity.id === activity.id;
  return {
    ...kept,
    completed: sameStage
      ? Math.max(counted(saved!.completed), counted(event.completed))
      : event.completed,
    activity:
      activity && sameStep
        ? {
            ...activity,
            stepCompleted: Math.max(
              counted(saved!.activity!.stepCompleted),
              counted(activity.stepCompleted),
            ),
            stepTotal: Math.max(counted(saved!.activity!.stepTotal), counted(activity.stepTotal)),
          }
        : activity,
    documentTypes: [...new Set([...(saved?.documentTypes ?? []), ...(event.documentTypes ?? [])])],
  };
}

/**
 * The saved events to fold in ahead of the live one (none when this setup has none), and
 * the progress run key: a different setup than the one saved starts its own run.
 */
export function useSetupProgressFloor(
  jobId: string,
  status: FolderAnalysisStatus | undefined,
  progress: FolderAnalysisProgress | undefined,
  finishing: boolean,
): { events: FolderAnalysisProgress[]; runKey: string } {
  const operationId = status?.operationId ?? null;
  // Read once per mount: this mount's own events are already in its progress state.
  const stored = useMemo(() => read(jobId), [jobId]);
  const latest = useRef<Saved | null>(stored);
  // Decided once, by the first operation this mount is told about. Before App's first
  // status read (the moment after a restart) the saved setup is the one on screen; if
  // the status then names a different setup, that setup starts its own run.
  const decision = useRef<'continue' | 'new' | null>(null);
  if (decision.current === null && operationId)
    decision.current = !stored || stored.operations.includes(operationId) ? 'continue' : 'new';
  const belongs =
    Boolean(stored) &&
    (decision.current === 'continue' || (decision.current === null && status === undefined));

  useEffect(() => {
    const done = finishing || progress?.stage === 'complete' || status?.state === 'ready';
    if (done) {
      latest.current = null;
      write(jobId, null);
      return;
    }
    if (!operationId) return;
    const saved = latest.current;
    // Every operation one mount is shown is the same setup (the screen's own state
    // already spans them), so each joins the saved one.
    const fresh = !saved || (saved === stored && decision.current === 'new');
    const operations = fresh ? [operationId] : [...new Set([...saved!.operations, operationId])];
    const keep = fresh ? null : saved!.event;
    let event = keep;
    if (
      progress &&
      !progress.heartbeat &&
      workingStageRank(progress.stage) >= 0 &&
      (!keep || workingStageRank(progress.stage) >= workingStageRank(keep.stage))
    )
      event = merge(keep && keep.stage === progress.stage ? keep : null, progress);
    if (!event) return;
    const steps = { ...(fresh ? {} : (saved!.steps ?? {})) };
    const stepId = progress?.activity?.id;
    if (
      progress &&
      !progress.heartbeat &&
      stepId?.startsWith('bootstrap:') &&
      counted(progress.activity!.stepTotal) > 0
    )
      steps[stepId] = merge(steps[stepId] ?? null, progress);
    if (
      event === saved?.event &&
      operations.length === saved!.operations.length &&
      steps[stepId ?? ''] === saved!.steps?.[stepId ?? '']
    )
      return;
    latest.current = { operations, event, steps };
    write(jobId, latest.current);
  }, [jobId, operationId, progress, finishing, status?.state]);

  // Stable objects: the progress reducer folds each event object once. The steps first,
  // the furthest event last, so it is the one the screen names.
  const events = useMemo(
    () => (belongs && stored ? [...Object.values(stored.steps ?? {}), stored.event] : []),
    [belongs, stored],
  );
  return {
    events,
    runKey: decision.current === 'new' ? `${jobId}:setup:${operationId}` : `${jobId}:setup`,
  };
}

/**
 * A setup that was cleared (Start over, choosing the documents again) is forgotten: its saved floor, its learning bar
 * and its progress run, so the next setup of the job starts from nothing. Kept, the next setup's screen opened on the
 * cleared one's count, and a count never goes down (October 7, 2026).
 */
export function forgetSetupProgress(jobId: string): void {
  write(jobId, null);
  try {
    localStorage.removeItem(learnKey(jobId));
  } catch {
    /* storage unavailable: nothing was kept */
  }
  forgetWorkingRuns(`${jobId}:setup`);
}

/**
 * Everything this window showed of a setup run that is cleared or started again (Start over,
 * choosing the documents again, Choose folder and retry after a failure): its floor, learning
 * bar and progress run, its last page, and what it learned. Kept, the retry opened on the
 * failed run's step and carried its results. Not for Read more, which only starts a new
 * progress run (forgetSetupProgress) and carries setup's results on. `keepResults`: an
 * unfinished run cleared while the previous setup is kept (Keep previous setup) keeps what
 * that setup learned (chunk 28, R4-2).
 */
export function forgetSetupRun(
  jobId: string,
  { keepResults = false }: { keepResults?: boolean } = {},
): void {
  forgetSetupProgress(jobId);
  forgetSetupPage(jobId);
  if (!keepResults) forgetSetupResults(jobId);
}

/**
 * The furthest the setup screen's learning bar has been drawn for this run, kept the same
 * way, so an app restart or a remount never draws it shorter (SetupJourney's two-part bar).
 * A different run (a new run key) starts from nothing.
 */
const learnKey = (jobId: string) => `cpv6-setup-learn-floor:${jobId}`;
const share = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
/** `learn`: the learning half of the bar; `read`: the reading stage's share (setup's own read, setup-journey-model.ts). */
export function readLearnFloor(jobId: string, runKey: string): { learn: number; read: number } {
  try {
    const saved = JSON.parse(localStorage.getItem(learnKey(jobId)) ?? 'null') as {
      runKey?: string;
      learn?: number;
      read?: number;
    } | null;
    return saved && saved.runKey === runKey
      ? { learn: share(saved.learn), read: share(saved.read) }
      : { learn: 0, read: 0 };
  } catch {
    return { learn: 0, read: 0 };
  }
}
export function writeLearnFloor(
  jobId: string,
  runKey: string,
  floor: { learn: number; read: number },
) {
  try {
    localStorage.setItem(
      learnKey(jobId),
      JSON.stringify({ runKey, learn: floor.learn, read: floor.read }),
    );
  } catch {
    /* storage unavailable: this mount still never goes back */
  }
}
